import type { ReactNode } from "react";
import { Drawer, type DrawerProps, type SxProps, type Theme } from "@mui/material";
import type { StackedDrawerProps } from "./types";

export interface StackedDrawerShellProps
  extends Pick<StackedDrawerProps, "open" | "onClose" | "onCloseAll" | "zIndex" | "depth"> {
  /** Content to render inside the drawer */
  children: ReactNode;
  /** ARIA label for accessibility */
  ariaLabel?: string;
  /** Custom width styles for the drawer paper */
  paperSx?: SxProps<Theme>;
  /** Dim the content behind the drawer instead of using a transparent backdrop */
  showBackdrop?: boolean;
}

/**
 * A drawer shell component designed to work within the DrawerStack system.
 * Uses an invisible backdrop so that MUI properly fires onClose with "backdropClick"
 * reason when the user clicks outside the drawer paper.
 *
 * Close behavior:
 * - Backdrop click (click outside) → closes the entire drawer stack
 * - ESC key → closes only this (topmost) drawer
 */
export function StackedDrawerShell({
  open,
  onClose,
  onCloseAll,
  zIndex,
  depth,
  children,
  ariaLabel,
  paperSx,
  showBackdrop = false,
}: StackedDrawerShellProps) {
  const handleClose: NonNullable<DrawerProps["onClose"]> = (_event, reason) => {
    if (reason === "backdropClick") {
      // Clicking outside the drawer closes the entire stack
      onCloseAll();
    } else if (reason === "escapeKeyDown") {
      // ESC closes topmost only
      onClose();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      role="dialog"
      aria-label={ariaLabel}
      // Use an invisible (transparent) backdrop so MUI fires onClose with "backdropClick"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: showBackdrop ? undefined : "transparent",
          },
        },
        paper: {
          sx: [
            {
              display: "flex",
              flexDirection: "column",
              height: "100vh",
              overflow: "hidden",
              // Slight visual offset for stacked drawers to hint at nesting
              ...(depth > 0 && {
                boxShadow: "-8px 0 24px rgba(0,0,0,0.15)",
              }),
            },
            ...(Array.isArray(paperSx) ? paperSx : paperSx ? [paperSx] : []),
          ],
        },
      }}
      sx={{ zIndex }}
      keepMounted={false}
    >
      {children}
    </Drawer>
  );
}

